import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { formatApiError } from "../lib/api";

export default function Auth() {
  const { t } = useLang();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(`${mode === "login" ? t("auth.login") : t("auth.register")} ✓`);
      const redirect = params.get("redirect");
      navigate(redirect || (user.role === "admin" ? "/admin" : "/account"));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-20" data-testid="auth-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="bg-white border border-[#E5D9C8] rounded-sm shadow-xl shadow-amber-900/5 overflow-hidden">
        <div className="grid grid-cols-2 border-b border-[#E5D9C8]">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(""); }} data-testid={`auth-tab-${m}`}
              className={`py-4 text-sm font-medium transition-colors ${mode === m ? "bg-bark text-parchment" : "text-cocoa/60 hover:text-honey"}`}>
              {m === "login" ? t("auth.login") : t("auth.register")}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="p-8 space-y-4">
          {mode === "register" && (
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("auth.name")}
              data-testid="auth-name-input" className="w-full bg-parchment border border-[#E5D9C8] px-4 py-3 text-sm focus:outline-none focus:border-honey" />
          )}
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("auth.email")}
            data-testid="auth-email-input" className="w-full bg-parchment border border-[#E5D9C8] px-4 py-3 text-sm focus:outline-none focus:border-honey" />
          <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t("auth.password")}
            data-testid="auth-password-input" className="w-full bg-parchment border border-[#E5D9C8] px-4 py-3 text-sm focus:outline-none focus:border-honey" />
          {error && <p className="text-sm text-terra" data-testid="auth-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="auth-submit-btn"
            className="w-full bg-bark text-parchment py-4 font-medium hover:bg-honey transition-colors duration-300 disabled:opacity-60">
            {loading ? t("common.loading") : mode === "login" ? t("auth.login.cta") : t("auth.register.cta")}
          </button>
          <p className="text-center text-sm text-cocoa/50">
            {mode === "login" ? t("auth.no.account") : t("auth.has.account")}{" "}
            <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="auth-switch-mode"
              className="text-honey font-medium underline underline-offset-4">
              {mode === "login" ? t("auth.register") : t("auth.login")}
            </button>
          </p>
        </form>
      </motion.div>
      <p className="text-center mt-6 text-sm text-cocoa/50"><Link to="/shop" className="hover:text-honey transition-colors">← {t("nav.shop")}</Link></p>
    </div>
  );
}
